import React, { useState, useEffect, useContext, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  CircularProgress,
  Container,
  Paper,
  Switch,
  FormControlLabel,
  Card,
  CardContent,
  CardActionArea,
  Chip,
  Stack,
  Grid,
  ToggleButton,
  ToggleButtonGroup,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  Chat as ChatIcon,
  CheckCircle,
  Cancel,
  RadioButtonUnchecked,
  ViewModule as GridViewIcon,
  ViewList as ListViewIcon,
  History as HistoryIcon,
  ExpandMore as ExpandMoreIcon,
  Category as CategoryIcon
} from '@mui/icons-material';
import { AuthContext } from '../../../contexts/AuthContext';
import axios from '../../../api/axios';
import StudentLayout from '../../../pages/Student/StudentLayout';
import DomeGallery from './DomeGallery';
import SubmissionHistoryDialog from './SubmissionHistoryDialog';

// 生成Mock数据
const generateMockConversations = (count = 30) => {
  const topics = [
    { name: '集合论', type: 'multiple_choice', preview: '对于任意集合 $A$，下面哪项是正确的？集合论是数学的基础分支之一，研究集合及其运算、关系和性质。' },
    { name: '逻辑推理', type: 'proof', preview: '证明：如果 $p \\rightarrow q$ 且 $q \\rightarrow r$，则 $p \\rightarrow r$。这是一个典型的逻辑推理问题，需要运用逻辑推理的基本规则。' },
    { name: '函数', type: 'short_answer', preview: '什么是函数的定义域和值域？请详细解释函数 $f(x) = \\sqrt{x-1}$ 的定义域和值域。' },
    { name: '算法', type: 'code', preview: '实现一个快速排序算法，并分析其时间复杂度。快速排序是一种高效的排序算法，平均时间复杂度为 O(n log n)。' },
    { name: '数据结构', type: 'code', preview: '如何使用链表实现栈？请提供完整的代码实现，包括入栈、出栈和查看栈顶元素的操作。' },
    { name: '概率论', type: 'multiple_choice', preview: '从一副52张的扑克牌中随机抽取一张，抽到红心的概率是多少？这是一个基础的概率计算问题。' },
    { name: '线性代数', type: 'short_answer', preview: '解释矩阵的秩的概念，并说明如何计算一个矩阵的秩。矩阵的秩是线性代数中的重要概念。' },
    { name: '微积分', type: 'proof', preview: '证明：$\\lim_{x \\to 0} \\frac{\\sin x}{x} = 1$。这是微积分中的一个重要极限，需要使用洛必达法则或泰勒展开来证明。' },
    { name: '图论', type: 'code', preview: '实现深度优先搜索（DFS）算法来遍历图。DFS是图论中的基础算法，用于遍历和搜索图结构。' },
    { name: '数论', type: 'proof', preview: '证明：对于任意正整数 $n$，$n^2 + n$ 总是偶数。这是一个数论中的基础证明题。' }
  ];

  const previews = [
    '这是一个关于数学基础的问题，需要理解基本概念和定理。',
    '这个问题涉及算法设计，需要考虑时间复杂度和空间复杂度。',
    '请仔细分析题目要求，逐步解答。',
    '这个问题需要运用多个知识点，建议先理解基本概念。',
    '代码实现时要注意边界条件和异常处理。',
    '证明题需要逻辑清晰，步骤完整。',
    '选择题可以通过排除法来提高正确率。',
    '简答题需要详细说明解题思路和步骤。',
    '代码追踪题需要仔细跟踪程序的执行流程。',
    '这个问题需要结合多个概念来解答。'
  ];

  const conversations = [];
  const now = Date.now();
  
  for (let i = 0; i < count; i++) {
    const topic = topics[i % topics.length];
    const daysAgo = Math.floor(Math.random() * 60);
    const lastMessageAt = new Date(now - daysAgo * 24 * 60 * 60 * 1000);
    const messageCount = 3 + Math.floor(Math.random() * 20);
    
    conversations.push({
      id: `mock_${i + 1}`,
      questionId: `q_${i + 1}`,
      questionTitle: `${topic.name}相关题目 ${Math.floor(i / topics.length) + 1}`,
      questionType: topic.type,
      preview: topic.preview + ' ' + previews[i % previews.length],
      messageCount: messageCount,
      lastMessageAt: lastMessageAt.toISOString()
    });
  }

  return conversations;
};

function MemoryPage() {
  const { user, selectedCourse } = useContext(AuthContext);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const notebookIdFromUrl = searchParams.get('notebook');

  const [conversations, setConversations] = useState([]);
  const [notebookExampleIds, setNotebookExampleIds] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingSubmissions, setLoadingSubmissions] = useState(false);
  const [error, setError] = useState(null);
  const [useMockData, setUseMockData] = useState(false); // 添加开关
  const [viewMode, setViewMode] = useState('gallery'); // 'gallery' 或 'list'
  const [submissionHistoryDialogOpen, setSubmissionHistoryDialogOpen] = useState(false);
  const [selectedQuestionSubmissions, setSelectedQuestionSubmissions] = useState([]);
  const [selectedQuestionInfo, setSelectedQuestionInfo] = useState(null);

  // 当 URL 带 ?notebook= 时拉取该笔记下的题目 id，用于过滤对话
  useEffect(() => {
    if (!notebookIdFromUrl) {
      setNotebookExampleIds([]);
      return;
    }
    const fetchNotebookExamples = async () => {
      try {
        const res = await axios.get(`/notebook-examples/notebook/${encodeURIComponent(notebookIdFromUrl)}`);
        const ids = (res.data?.examples || []).map((e) => e.id);
        setNotebookExampleIds(ids);
      } catch (err) {
        console.warn('Failed to fetch notebook examples for filter:', err);
        setNotebookExampleIds([]);
      }
    };
    fetchNotebookExamples();
  }, [notebookIdFromUrl]);

  useEffect(() => {
    const fetchConversations = async () => {
      try {
        setError(null);
        
        // 如果使用mock数据
        if (useMockData) {
          // 模拟加载延迟
          await new Promise(resolve => setTimeout(resolve, 500));
          const mockData = generateMockConversations(50); // 生成50条mock数据
          setConversations(mockData);
        } else {
          const params = {};
          if (selectedCourse?.id) {
            params.courseTemplateId = selectedCourse.id;
          }
          const response = await axios.get('/notebook-examples/chat/conversations', { params });
          const conversationsData = response.data || [];
          console.log('[MemoryPage] Fetched conversations:', conversationsData.length, conversationsData);
          setConversations(conversationsData);
        }
      } catch (error) {
        console.error('Error fetching conversations:', error);
        setError(error.response?.data?.error || error.message || '获取对话记录失败');
      } finally {
        setLoading(false);
      }
    };

    fetchConversations();
  }, [user, useMockData, selectedCourse]);

  // 当 URL 带 ?notebook= 时只显示该笔记题目下的对话
  const displayedConversations = useMemo(() => {
    if (!notebookIdFromUrl || !notebookExampleIds.length) return conversations;
    const idSet = new Set(notebookExampleIds);
    return conversations.filter((c) => {
      const qId = c.questionId?.toString();
      if (!qId || !qId.startsWith('notebook_')) return false;
      const exId = parseInt(qId.replace('notebook_', ''), 10);
      return idSet.has(exId);
    });
  }, [conversations, notebookIdFromUrl, notebookExampleIds]);

  useEffect(() => {
    const fetchSubmissions = async () => {
      try {
        setLoadingSubmissions(true);
        const params = {};
        if (selectedCourse?.id) {
          params.courseTemplateId = selectedCourse.id;
        }
        const response = await axios.get('/submissions/my-submissions', { params });
        setSubmissions(response.data || []);
      } catch (error) {
        console.error('Error fetching submissions:', error);
      } finally {
        setLoadingSubmissions(false);
      }
    };

    fetchSubmissions();
  }, [user, selectedCourse]);

  const handleConversationClick = (conversation) => {
    navigate(`/memory/${conversation.id}`);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const daysAgo = Math.floor((Date.now() - date.getTime()) / (1000 * 60 * 60 * 24));
    if (daysAgo === 0) return '今天';
    if (daysAgo === 1) return '昨天';
    if (daysAgo < 7) return `${daysAgo}天前`;
    return date.toLocaleDateString('zh-CN', { month: '2-digit', day: '2-digit' });
  };


  // 按题目分组所有提交记录
  const getGroupedSubmissions = () => {
    const groupedMap = new Map();
    submissions.forEach(sub => {
      // Handle both object format (from API) and string/number format
      let questionId;
      if (typeof sub.questionId === 'object' && sub.questionId !== null) {
        questionId = sub.questionId._id || sub.questionId;
      } else {
        questionId = sub.questionId;
      }
      const questionIdStr = questionId?.toString() || questionId;
      
      if (!groupedMap.has(questionIdStr)) {
        groupedMap.set(questionIdStr, {
          questionId: questionIdStr,
          questionInfo: typeof sub.questionId === 'object' && sub.questionId !== null 
            ? sub.questionId 
            : { _id: questionId, title: sub.questionTitle, category: sub.category, questionNumber: sub.questionNumber },
          submissions: []
        });
      }
      
      groupedMap.get(questionIdStr).submissions.push(sub);
    });
    
    // 对每个题目的提交记录按时间排序（最新的在前）
    groupedMap.forEach(group => {
      group.submissions.sort((a, b) => new Date(b.submittedAt) - new Date(a.submittedAt));
    });
    
    // 转换为数组并按最新提交时间排序
    return Array.from(groupedMap.values()).sort((a, b) => {
      const latestA = a.submissions[0]?.submittedAt;
      const latestB = b.submissions[0]?.submittedAt;
      return new Date(latestB) - new Date(latestA);
    });
  };

  const groupedSubmissions = getGroupedSubmissions();

  // 当 URL 带 ?notebook= 时只显示该笔记题目下的做题记录
  const displayedGroupedSubmissions = useMemo(() => {
    if (!notebookIdFromUrl || !notebookExampleIds.length) return groupedSubmissions;
    const idSet = new Set(notebookExampleIds);
    return groupedSubmissions.filter((group) => {
      const qId = group.questionId?.toString();
      if (!qId || !qId.startsWith('notebook_')) return false;
      const exId = parseInt(qId.replace('notebook_', ''), 10);
      return idSet.has(exId);
    });
  }, [groupedSubmissions, notebookIdFromUrl, notebookExampleIds]);

  const getStatusIcon = (status) => {
    if (!status) return <RadioButtonUnchecked color="disabled" />;
    if (status === 'passed') return <CheckCircle color="success" />;
    return <Cancel color="error" />;
  };

  // 将对话记录转换为 DomeGallery 需要的格式（使用按笔记本过滤后的列表）
  const conversationImages = displayedConversations.map((conv) => {
    // 根据题目类型生成不同的图片
    const imageSeed = conv.id || Math.random();
    const imageSize = 300 + (conv.messageCount || 0) * 10;
    const imageUrl = `https://picsum.photos/seed/${encodeURIComponent(conv.questionTitle || imageSeed)}/${imageSize}/${imageSize}?grayscale`;
    
    // 限制预览文本长度，确保时间信息可见
    const preview = conv.preview || '点击查看对话';
    const maxPreviewLength = 50; // 限制预览文本长度
    const truncatedPreview = preview.length > maxPreviewLength 
      ? preview.substring(0, maxPreviewLength) + '...' 
      : preview;
    
    return {
      src: imageUrl,
      image: imageUrl, // 兼容两种格式
      alt: conv.questionTitle || '未命名题目',
      title: conv.questionTitle || '未命名题目',
      link: `/memory/${conv.id}`,
      description: truncatedPreview,
      meta: `${formatDate(conv.lastMessageAt)} • ${conv.messageCount || 0} 条消息`, // 时间信息单独显示
      type: 'conversation',
      data: conv // 保留原始数据
    };
  });

  // 将做题记录转换为 DomeGallery 需要的格式（使用按笔记本过滤后的列表）
  const submissionImages = displayedGroupedSubmissions.map((group) => {
    const latestSub = group.submissions[0];
    const sub = latestSub;
    const questionTitle = group.questionInfo.title || '未命名题目';
    const questionId = group.questionId;
    const imageSize = 300 + (sub.score || 0) * 2;
    const imageUrl = `https://picsum.photos/seed/${encodeURIComponent(questionTitle + sub.id)}/${imageSize}/${imageSize}?grayscale`;
    
    const questionIdStr = questionId?.toString() || questionId;
    const link = questionIdStr && questionIdStr.startsWith('notebook_') 
      ? `/notebook-exercise/${questionIdStr.replace('notebook_', '')}`
      : `/question/${questionIdStr}`;
    
    return {
      src: imageUrl,
      image: imageUrl,
      alt: questionTitle,
      title: questionTitle,
      link: link,
      description: `分数: ${sub.score} • ${sub.status === 'passed' ? '通过' : sub.status === 'failed' ? '未通过' : '待评分'}`,
      meta: `${formatDate(sub.submittedAt)} • ${group.submissions.length} 次提交`,
      type: 'submission',
      data: group // 保留整个组的数据，包含所有提交记录
    };
  });

  // 合并对话记录和做题记录，按时间排序（最新的在前面）
  const galleryImages = [...conversationImages, ...submissionImages].sort((a, b) => {
    const timeA = a.type === 'conversation' ? a.data.lastMessageAt : a.data.submittedAt;
    const timeB = b.type === 'conversation' ? b.data.lastMessageAt : b.data.submittedAt;
    const timeAValue = timeA ? new Date(timeA).getTime() : 0;
    const timeBValue = timeB ? new Date(timeB).getTime() : 0;
    return timeBValue - timeAValue; // 降序：最新的在前
  });

  const handleItemClick = (item) => {
    // 从 item 中找到对应的记录
    const record = galleryImages.find(img => img.src === item.src || img.image === item.src);
    if (record) {
      if (record.type === 'conversation') {
        handleConversationClick(record.data);
      } else if (record.type === 'submission') {
        // 对于做题记录，打开提交历史对话框
        handleViewSubmissionHistory(record.data);
      }
    } else if (item.link) {
      // 如果没有找到记录，直接使用 link
      navigate(item.link);
    }
  };

  const handleViewSubmissionHistory = (group) => {
    setSelectedQuestionInfo(group.questionInfo);
    setSelectedQuestionSubmissions(group.submissions);
    setSubmissionHistoryDialogOpen(true);
  };

  const handleCloseSubmissionHistory = () => {
    setSubmissionHistoryDialogOpen(false);
    setSelectedQuestionSubmissions([]);
    setSelectedQuestionInfo(null);
  };

  // 合并对话记录和做题记录，按时间排序
  const getAllRecords = () => {
    const records = [];
    
    // 添加对话记录（使用按笔记本过滤后的列表）
    console.log('[MemoryPage] Adding conversations to records:', displayedConversations.length);
    displayedConversations.forEach(conv => {
      const record = {
        type: 'conversation',
        id: conv.id,
        title: conv.questionTitle || '未命名题目',
        questionType: conv.questionType,
        category: conv.category || null, // 使用后端返回的 category
        preview: conv.preview,
        time: conv.lastMessageAt,
        meta: `${conv.messageCount || 0} 条消息`,
        data: conv
      };
      console.log('[MemoryPage] Adding conversation record:', record);
      records.push(record);
    });
    
    // 添加做题记录（使用按笔记本过滤后的列表）
    displayedGroupedSubmissions.forEach(group => {
      const sub = group.submissions[0]; // 使用最新提交
      records.push({
        type: 'submission',
        id: sub.id,
        title: group.questionInfo.title || '未命名题目',
        questionType: null,
        category: group.questionInfo.category,
        questionNumber: group.questionInfo.questionNumber,
        status: sub.status,
        score: sub.score,
        time: sub.submittedAt,
        meta: `分数: ${sub.score} • ${group.submissions.length} 次提交`,
        data: group // 保存整个组，包含所有提交记录
      });
    });
    
    // 按时间排序，最新的在前面（确保时间字段有效）
    return records.sort((a, b) => {
      const timeA = a.time ? new Date(a.time).getTime() : 0;
      const timeB = b.time ? new Date(b.time).getTime() : 0;
      return timeB - timeA; // 降序：最新的在前
    });
  };

  const allRecords = getAllRecords();
  console.log('[MemoryPage] All records:', allRecords.length, allRecords);

  // 按专题分组记录
  const getRecordsByCategory = () => {
    const categoryMap = new Map();
    
    allRecords.forEach(record => {
      const category = record.category || '其他';
      console.log('[MemoryPage] Grouping record:', record.type, 'into category:', category);
      
      if (!categoryMap.has(category)) {
        categoryMap.set(category, []);
      }
      
      categoryMap.get(category).push(record);
    });
    
    // 转换为数组，按专题名称排序
    return Array.from(categoryMap.entries())
      .map(([category, records]) => ({
        category,
        records: records.sort((a, b) => {
          const timeA = a.time ? new Date(a.time).getTime() : 0;
          const timeB = b.time ? new Date(b.time).getTime() : 0;
          return timeB - timeA; // 每个专题内也按时间排序
        })
      }))
      .sort((a, b) => {
        // 专题排序：有名称的在前，按字母顺序；"其他"在最后
        if (a.category === '其他') return 1;
        if (b.category === '其他') return -1;
        return a.category.localeCompare(b.category, 'zh-CN');
      });
  };

  const recordsByCategory = getRecordsByCategory();
  console.log('[MemoryPage] Records by category:', recordsByCategory);

  if (loading) {
    return (
      <StudentLayout appBarTitle={notebookIdFromUrl ? '本笔记本记忆' : '对话记录 (Memory)'}>
        <Container maxWidth="xl">
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        </Container>
      </StudentLayout>
    );
  }

  if (error) {
    return (
      <StudentLayout appBarTitle={notebookIdFromUrl ? '本笔记本记忆' : '对话记录 (Memory)'}>
        <Container maxWidth="xl">
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
            <Typography color="error" sx={{ mb: 2 }}>
              加载失败: {error}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              请检查网络连接或稍后重试
            </Typography>
          </Box>
        </Container>
      </StudentLayout>
    );
  }


  return (
    <StudentLayout appBarTitle="对话记录 (Memory)">
      <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1 }}>
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography variant="h5" sx={{ mb: 1 }}>
              学习记录 ({allRecords.length})
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {viewMode === 'gallery' ? '点击卡片查看完整对话内容' : '点击卡片查看详情'}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={(e, newMode) => {
                if (newMode !== null) {
                  setViewMode(newMode);
                }
              }}
              size="small"
            >
              <ToggleButton value="gallery" aria-label="画廊视图">
                <GridViewIcon sx={{ mr: 1 }} />
                画廊视图
              </ToggleButton>
              <ToggleButton value="list" aria-label="列表视图">
                <ListViewIcon sx={{ mr: 1 }} />
                列表视图
              </ToggleButton>
            </ToggleButtonGroup>
            <FormControlLabel
              control={
                <Switch
                  checked={useMockData}
                  onChange={(e) => setUseMockData(e.target.checked)}
                  size="small"
                />
              }
              label="使用Mock数据"
            />
          </Box>
        </Box>

        {loading || loadingSubmissions ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : allRecords.length === 0 ? (
          <Paper sx={{ p: 6, textAlign: 'center' }}>
            <ChatIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
              {notebookIdFromUrl ? '本笔记下暂无对话与做题记录' : '还没有学习记录'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2 }}>
              {notebookIdFromUrl ? '在本笔记题目下与 AI 对话或提交答案后，记录会显示在这里' : '去题目页面开始学习和做题吧'}
            </Typography>
          </Paper>
        ) : viewMode === 'gallery' ? (
          <Box sx={{ 
            height: 'calc(100vh - 300px)',
            minHeight: '600px',
            position: 'relative', 
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <DomeGallery
              images={galleryImages}
              fit={0.4}
              minRadius={500}
              maxVerticalRotationDeg={5}
              segments={20}
              dragDampening={2}
              grayscale={true}
              onItemClick={handleItemClick}
            />
          </Box>
        ) : (
          <Box>
            {recordsByCategory.length === 0 ? (
              <Paper sx={{ p: 6, textAlign: 'center' }}>
                <ChatIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                  还没有学习记录
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2 }}>
                  去题目页面开始学习和做题吧
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  调试信息: conversations={displayedConversations.length}, submissions={submissions.length}, allRecords={allRecords.length}
                </Typography>
              </Paper>
            ) : (
              recordsByCategory.map(({ category, records }) => (
                <Accordion 
                key={category} 
                defaultExpanded={recordsByCategory.length <= 3}
                sx={{ mb: 2 }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    bgcolor: 'background.default',
                    '&:hover': {
                      bgcolor: 'action.hover'
                    }
                  }}
                >
                  <Stack direction="row" spacing={2} alignItems="center" sx={{ width: '100%' }}>
                    <CategoryIcon color="primary" />
                    <Typography variant="h6" sx={{ flexGrow: 1 }}>
                      {category}
                    </Typography>
                    <Chip 
                      label={`${records.length} 条记录`} 
                      size="small" 
                      color="primary" 
                      variant="outlined"
                    />
                  </Stack>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2}>
                    {records.map((record) => (
                      <Grid item xs={12} sm={6} md={4} key={`${record.type}-${record.id}`}>
                        <Card 
                          variant="outlined"
                          sx={{
                            height: '100%',
                            transition: 'all 0.2s ease-in-out',
                            '&:hover': {
                              boxShadow: 4,
                              transform: 'translateY(-2px)',
                            }
                          }}
                        >
                          <CardActionArea 
                            onClick={() => {
                              if (record.type === 'conversation') {
                                handleConversationClick(record.data);
                              } else {
                                // 对于做题记录，打开提交历史对话框
                                handleViewSubmissionHistory(record.data);
                              }
                            }}
                            sx={{ height: '100%' }}
                          >
                            <CardContent>
                              <Stack spacing={1}>
                                <Stack direction="row" spacing={1} alignItems="center">
                                  {record.type === 'conversation' ? (
                                    <ChatIcon fontSize="small" color="primary" />
                                  ) : (
                                    getStatusIcon(record.status)
                                  )}
                                  <Chip
                                    label={record.type === 'conversation' ? '对话记录' : '做题记录'}
                                    size="small"
                                    color={record.type === 'conversation' ? 'primary' : 'secondary'}
                                    variant="outlined"
                                  />
                                </Stack>
                                <Typography variant="h6" component="div" sx={{ fontWeight: 600 }} noWrap>
                                  {record.title}
                                </Typography>
                                {record.type === 'conversation' && record.preview && (
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{
                                      display: '-webkit-box',
                                      WebkitLineClamp: 2,
                                      WebkitBoxOrient: 'vertical',
                                      overflow: 'hidden',
                                    }}
                                  >
                                    {record.preview}
                                  </Typography>
                                )}
                                {record.type === 'submission' && (
                                  <Stack direction="row" spacing={1} flexWrap="wrap">
                                    {record.questionNumber && (
                                      <Chip
                                        label={record.questionNumber}
                                        size="small"
                                        variant="outlined"
                                      />
                                    )}
                                  </Stack>
                                )}
                                {record.questionType && (
                                  <Chip
                                    label={
                                      record.questionType === 'multiple_choice' ? '选择题' :
                                      record.questionType === 'short_answer' ? '简答题' :
                                      record.questionType === 'proof' ? '证明题' :
                                      record.questionType === 'code' ? '代码题' :
                                      record.questionType === 'code_tracing' ? '代码追踪题' :
                                      record.questionType
                                    }
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                  />
                                )}
                                {record.type === 'submission' && (
                                  <>
                                    <Chip
                                      label={record.status === 'passed' ? '通过' : record.status === 'failed' ? '未通过' : '待评分'}
                                      size="small"
                                      color={record.status === 'passed' ? 'success' : record.status === 'failed' ? 'error' : 'warning'}
                                      icon={getStatusIcon(record.status)}
                                    />
                                    {record.data && record.data.submissions && record.data.submissions.length > 1 && (
                                      <Chip
                                        label={`${record.data.submissions.length} 次提交`}
                                        size="small"
                                        variant="outlined"
                                        icon={<HistoryIcon fontSize="small" />}
                                      />
                                    )}
                                  </>
                                )}
                                <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 'auto', pt: 1 }}>
                                  <Typography variant="caption" color="text.secondary">
                                    {formatDate(record.time)}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    • {record.meta}
                                  </Typography>
                                </Stack>
                              </Stack>
                            </CardContent>
                          </CardActionArea>
                        </Card>
                      </Grid>
                      ))}
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              ))
            )}
          </Box>
        )}

        {/* Submission History Dialog */}
        <SubmissionHistoryDialog
          open={submissionHistoryDialogOpen}
          onClose={handleCloseSubmissionHistory}
          questionInfo={selectedQuestionInfo}
          submissions={selectedQuestionSubmissions}
        />
      </Container>
    </StudentLayout>
  );
}

export default MemoryPage;
